'use client';
import logger from '../../logger';
import interactionLedger from '../../utils/interactionLedger';

/**
 * createOfflineFirstService — factory for the offline-first cache pattern.
 *
 * Every required-app-data entity (notes, notebooks, userSettings, etc.)
 * follows the same flow:
 *   1. Check IndexedDB for cached data
 *   2. If found  → return it immediately (source: 'local')
 *   3. If missing → fetch from cloud (source: 'cloud')
 *   4. Throw on unrecoverable error (XState catches in onError)
 *
 * Usage (in appState_requiredAppDataState.js):
 *
 *   invoke: {
 *     src: createOfflineFirstService({
 *       entityName: 'notes',
 *       checkLocalDB: checkNotesLocalDatabaseContent,
 *       hasLocalData: (result) => result.hasNotesLocalDatabaseContent,
 *       fetchFromCloud: fetchNotesFromCloud,
 *     }),
 *     onDone: { ... entity-specific assign ... },
 *     onError: { ... },
 *   }
 *
 * The resolved value shape:
 *   { source: 'local'|'cloud', localData?: any, cloudData?: any }
 *
 * @param {object} options
 * @param {string}   options.entityName    - Used for logging and ledger entries
 * @param {Function} options.checkLocalDB  - async () => checkResult
 * @param {Function} options.hasLocalData  - (checkResult) => boolean
 * @param {Function} options.fetchFromCloud - async () => cloudData
 */
export const createOfflineFirstService = ({
  entityName,
  checkLocalDB,
  hasLocalData,
  fetchFromCloud,
}) => {
  return async () => {
    // --- Phase 1: Check local IndexedDB ---
    let checkResult;
    try {
      checkResult = await checkLocalDB();
      logger.info({ entityName, hasData: hasLocalData(checkResult) }, 'offlineFirstActor: checkLocalDB done');
    } catch (error) {
      logger.error(error, `offlineFirstActor: checkLocalDB failed for ${entityName}`);
      try {
        interactionLedger.log('apiError', 'appState', {
          service: entityName,
          stage: 'checkLocalDB',
          error: error?.message || String(error),
          errorType: 'localDBError',
          errorStack: error?.stack,
        }, `Failed to check local database for ${entityName}: ${error?.message || 'Unknown error'}`);
      } catch (_) {}
      throw error;
    }

    // --- Phase 2: Cache hit — return local data ---
    if (hasLocalData(checkResult)) {
      logger.info({ entityName }, 'offlineFirstActor: cache hit, returning local data');
      return { source: 'local', localData: checkResult };
    }

    // --- Phase 3: Cache miss — fetch from cloud ---
    logger.info({ entityName }, 'offlineFirstActor: cache miss, fetching from cloud');
    try {
      const cloudData = await fetchFromCloud();
      logger.info({ entityName }, 'offlineFirstActor: cloud fetch done');
      return { source: 'cloud', cloudData };
    } catch (error) {
      logger.warn(error, `offlineFirstActor: cloud fetch failed for ${entityName} — continuing offline`);
      try {
        interactionLedger.log('apiError', 'appState', {
          service: entityName,
          stage: 'fetchFromCloud',
          error: error?.message || String(error),
          errorType: error?.errorType || 'apiError',
          path: error?.path,
          status: error?.status,
          errorStack: error?.stack,
        }, `Offline: Failed to fetch ${entityName} from cloud - ${error?.message || 'Unknown error'}`);
      } catch (_) {}
      // Don't throw — return offline source so the app can continue with empty/default data
      return { source: 'offline', cloudData: null };
    }
  };
};
